import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowUp, Headset, Loader2, Mic, RotateCcw, Square, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVoiceInput, type VoiceError } from "@/hooks/useVoiceInput";

type Msg = { role: "user" | "assistant"; content: string; error?: boolean };

const STORAGE_KEY = "prebitit-assistant-chat";
const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-assistant`;
const HIDDEN_PREFIXES = ["/dashboard", "/admin", "/.lovable"];

const BotMark = ({ className }: { className?: string }) => (
  <span className={cn("grid shrink-0 place-items-center overflow-hidden rounded-full shadow-md", className)} aria-hidden>
    <img
      src="/images/logo.png"
      alt=""
      className="h-full w-full object-cover"
      width={64}
      height={64}
      decoding="async"
    />
  </span>
);

const AIChatSection = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [mobileViewport, setMobileViewport] = useState<{ top: number; height: number } | null>(null);
  const lang = (i18n.language || "en").slice(0, 2);
  const rtl = lang === "ar";

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.filter((m) => !m.error)));
  }, [messages]);

  useEffect(() => {
    const messagesEl = messagesRef.current;
    if (!messagesEl) return;
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  useEffect(() => {
    if (!open) {
      setMobileViewport(null);
      return;
    }

    const scrollY = window.scrollY;
    const previous = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    const syncViewport = () => {
      if (!window.matchMedia("(max-width: 639px)").matches) {
        setMobileViewport(null);
        return;
      }
      const viewport = window.visualViewport;
      setMobileViewport({
        top: viewport?.offsetTop ?? 0,
        height: viewport?.height ?? window.innerHeight,
      });
      requestAnimationFrame(() => {
        const messagesEl = messagesRef.current;
        if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight;
      });
    };

    syncViewport();
    window.visualViewport?.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("scroll", syncViewport);
    window.addEventListener("resize", syncViewport);

    return () => {
      window.visualViewport?.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("scroll", syncViewport);
      window.removeEventListener("resize", syncViewport);
      document.body.style.overflow = previous.overflow;
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (open && !loading) inputRef.current?.focus();
  }, [open, loading]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const [voiceErr, setVoiceErr] = useState("");
  const onVoiceText = useCallback((text: string) => {
    setInput((prev) => (prev ? prev.trimEnd() + " " : "") + text);
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 50);
  }, []);
  const onVoiceError = useCallback((k: VoiceError) => setVoiceErr(t(`chat.${k}`)), [t]);
  const voice = useVoiceInput(lang, onVoiceText, onVoiceError);

  useEffect(() => { if (!open) voice.cancel(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || loading) return;
    const history: Msg[] = [...messages.filter((m) => !m.error), { role: "user", content }];
    setMessages([...history, { role: "assistant", content: "" }]);
    setInput("");
    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages: history, lang, page: location.pathname }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const key = res.status === 429 ? "chat.errorBusy" : "chat.errorGeneric";
        throw new Error(t(key));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages([...history, { role: "assistant", content: acc }]);
      }
      if (!acc.trim()) throw new Error(t("chat.errorGeneric"));
    } catch (e: any) {
      if (e?.name === "AbortError") {
        setMessages((prev) => (prev[prev.length - 1]?.content ? prev : prev.slice(0, -1)));
      } else {
        setMessages([...history, { role: "assistant", content: e?.message || t("chat.errorGeneric"), error: true }]);
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const suggestions = [t("chat.suggest1"), t("chat.suggest2"), t("chat.suggest3"), t("chat.suggest4")];
  const last = messages[messages.length - 1];
  const waiting = loading && last?.role === "assistant" && !last.content;

  const renderLink = ({ href = "", children }: any) => {
    const internal = href.startsWith("/") || /^https?:\/\/(www\.)?prebitit\.com/.test(href);
    if (internal) {
      const path = href.replace(/^https?:\/\/(www\.)?prebitit\.com/, "") || "/";
      return (
        <a
          href={path}
          onClick={(e) => {
            e.preventDefault();
            navigate(path);
            if (window.innerWidth < 640) setOpen(false);
          }}
          className="font-medium text-primary underline underline-offset-2"
        >
          {children}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2">
        {children}
      </a>
    );
  };

  return (
    <div dir={rtl ? "rtl" : "ltr"} className="notranslate" translate="no">
      <AnimatePresence>
        {open && (
          <motion.section
            key="panel"
            role="dialog"
            aria-label={t("chat.title")}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            style={mobileViewport ? ({
              "--chat-viewport-top": `${mobileViewport.top}px`,
              "--chat-viewport-height": `${mobileViewport.height}px`,
            } as CSSProperties) : undefined}
            className={cn(
              "fixed z-50 flex min-h-0 flex-col overflow-hidden overscroll-none border border-border/60 bg-background/95 shadow-2xl backdrop-blur-xl",
              "inset-x-0 top-[var(--chat-viewport-top,0px)] h-[var(--chat-viewport-height,100dvh)] rounded-none",
              "sm:inset-x-auto sm:top-auto sm:bottom-24 sm:h-[min(620px,calc(100dvh-8rem))] sm:w-[400px]",
              "sm:rounded-3xl",
              rtl ? "sm:left-6" : "sm:right-6",
            )}
          >
            {/* Header */}
            <header className="flex shrink-0 items-center gap-3 border-b border-border/60 bg-gradient-to-br from-primary/10 to-transparent px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
              <div className="relative">
                <BotMark className="h-10 w-10 text-base" />
                <span className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 border-background bg-emerald-500" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-sm font-semibold text-foreground">{t("chat.title")}</h2>
                <p className="truncate text-xs text-muted-foreground">{t("chat.subtitle")}</p>
              </div>
              {messages.length > 0 && (
                <button
                  onClick={() => { abortRef.current?.abort(); setMessages([]); }}
                  className="rounded-full p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t("chat.newChat")}
                  title={t("chat.newChat")}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t("chat.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            {/* Messages */}
            <div
              ref={messagesRef}
              className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain px-4 py-5 [-webkit-overflow-scrolling:touch]"
              aria-live="polite"
              onWheel={(event) => event.stopPropagation()}
              onTouchMove={(event) => event.stopPropagation()}
            >
              {messages.length === 0 && (
                <div className="flex flex-col items-center pt-4 text-center">
                  <BotMark className="mb-3 h-14 w-14 text-xl shadow-lg" />
                  <p className="text-base font-semibold text-foreground">{t("chat.welcomeTitle")}</p>
                  <p className="mt-1 max-w-[18rem] text-sm text-muted-foreground">{t("chat.welcomeText")}</p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium text-foreground transition hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 active:translate-y-0"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) =>
                m.role === "user" ? (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end">
                    <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-3xl rounded-ee-md bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-sm">
                      {m.content}
                    </div>
                  </motion.div>
                ) : m.content ? (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2.5">
                    <BotMark className="mt-0.5 h-7 w-7 shrink-0 text-xs" />
                    <div
                      className={cn(
                        "prose prose-sm max-w-[85%] break-words text-sm text-foreground dark:prose-invert prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5",
                        m.error && "rounded-2xl bg-destructive/10 px-3 py-2 text-destructive",
                      )}
                    >
                      <ReactMarkdown components={{ a: renderLink }}>{m.content}</ReactMarkdown>
                    </div>
                  </motion.div>
                ) : null,
              )}

              {waiting && (
                <div className="flex items-center gap-2.5" aria-label={t("chat.typing")}>
                  <BotMark className="h-7 w-7 text-xs" />
                  <div className="flex gap-1 rounded-full bg-muted px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="h-1.5 w-1.5 rounded-full bg-muted-foreground"
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                        transition={{ duration: 1, repeat: Infinity, delay: d * 0.15 }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="shrink-0 border-t border-border/60 bg-background/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            >
              {voiceErr && (
                <p role="alert" className="mb-2 rounded-2xl bg-destructive/10 px-3 py-2 text-xs text-destructive">{voiceErr}</p>
              )}
              <div className={cn(
                "flex items-end gap-2 rounded-3xl border bg-card px-2 py-1.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20",
                voice.state === "recording" ? "border-destructive/60 ring-2 ring-destructive/20" : "border-border",
              )}>
                {voice.state === "idle" ? (
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value.slice(0, 2000))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
                    }}
                    rows={1}
                    placeholder={t("chat.placeholder")}
                    aria-label={t("chat.placeholder")}
                    className="max-h-32 min-h-[36px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  />
                ) : (
                  <div className="flex min-h-[36px] min-w-0 flex-1 items-center gap-2 px-2" aria-live="assertive">
                    {voice.state === "recording" ? (
                      <>
                        <span className="relative flex h-2.5 w-2.5 shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
                        </span>
                        <div className="flex h-6 min-w-0 flex-1 items-center gap-[3px] overflow-hidden" aria-hidden>
                          {Array.from({ length: 14 }).map((_, i) => (
                            <motion.span
                              key={i}
                              className="w-[3px] shrink-0 rounded-full bg-destructive/80"
                              animate={{ height: [4, 4 + Math.min(1, voice.level) * 18 * (0.5 + ((i * 7) % 5) / 8), 4] }}
                              transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.04 }}
                            />
                          ))}
                        </div>
                        <span className="shrink-0 text-xs text-muted-foreground">{t("chat.listening")}</span>
                      </>
                    ) : (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span className="truncate text-xs text-muted-foreground">{t("chat.transcribing")}</span>
                      </>
                    )}
                  </div>
                )}
                {!loading && (
                  <button
                    type="button"
                    onClick={() => { setVoiceErr(""); voice.state === "recording" ? voice.stop() : voice.state === "idle" && voice.start(); }}
                    disabled={voice.state === "transcribing"}
                    className={cn(
                      "grid h-9 w-9 shrink-0 place-items-center rounded-full transition hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:hover:scale-100",
                      voice.state === "recording" ? "bg-destructive text-destructive-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                    aria-label={voice.state === "recording" ? t("chat.voiceStop") : t("chat.voice")}
                    title={voice.state === "recording" ? t("chat.voiceStop") : t("chat.voice")}
                    aria-pressed={voice.state === "recording"}
                  >
                    {voice.state === "recording" ? <Square className="h-3.5 w-3.5 fill-current" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
                {loading ? (
                  <button
                    type="button"
                    onClick={() => abortRef.current?.abort()}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted text-foreground transition hover:bg-muted/70"
                    aria-label={t("chat.stop")}
                  >
                    <Square className="h-3.5 w-3.5 fill-current" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!input.trim() || voice.state !== "idle"}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
                    aria-label={t("chat.send")}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("chat.disclaimer")}</p>
            </form>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Launcher */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        aria-label={open ? t("chat.close") : t("chat.open")}
        aria-expanded={open}
        className={cn(
          "fixed bottom-4 z-50 grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-xl ring-4 ring-primary/15 focus-visible:outline-none focus-visible:ring-primary/40 sm:bottom-6",
          rtl ? "left-4 sm:left-6" : "right-4 sm:right-6",
          open && "max-sm:hidden",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "x" : "chat"}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {open ? <X className="h-6 w-6" /> : <Headset className="h-6 w-6" />}
          </motion.span>
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default AIChatSection;
